/** Customer-facing explainability presentation. Does not invent backend evidence. */

const CONSTRUCT_LABELS = {
  party: "Party size",
  stay: "Observed stay duration",
  lead: "Booking lead time",
  recency: "Booking recency",
  monetary: "Transaction value",
  frequency: "Booking frequency",
  duration: "Observed duration",
  gap: "Observed interval",
  calendar: "Calendar-month pattern",
  size: "Generic size",
};

const DIRECTION_FACT = {
  party: { higher: "larger party sizes", lower: "smaller party sizes" },
  stay: { higher: "longer stays", lower: "shorter stays" },
  lead: { higher: "longer booking lead time", lower: "shorter booking lead time" },
  recency: { higher: "more lapsed activity", lower: "more recent activity" },
  monetary: { higher: "higher transaction value", lower: "lower transaction value" },
  frequency: { higher: "more frequent bookings", lower: "less frequent bookings" },
  duration: { higher: "longer observed duration", lower: "shorter observed duration" },
  gap: { higher: "longer interval", lower: "shorter interval" },
  calendar: { higher: "a calendar-month pattern", lower: "a calendar-month pattern" },
  size: { higher: "larger recorded size", lower: "smaller recorded size" },
};

const DIRECTION_WORD = {
  party: { higher: "Higher", lower: "Lower" },
  stay: { higher: "Longer", lower: "Shorter" },
  lead: { higher: "Longer", lower: "Shorter" },
  recency: { higher: "More lapsed", lower: "More recent" },
  monetary: { higher: "Higher", lower: "Lower" },
  frequency: { higher: "More frequent", lower: "Less frequent" },
  duration: { higher: "Longer", lower: "Shorter" },
  gap: { higher: "Longer", lower: "Shorter" },
  size: { higher: "Higher", lower: "Lower" },
};

const INTERNAL_LEAK = [
  "representation_id",
  "representation_family_id",
  "operator_family",
  "quantile_upper",
  "quantile_lower",
  "partition_3",
  "parent_a",
  "parent_b",
  "candidate_id",
  "cliffs_delta",
  "size__pax",
];

export const explainabilityOf = (row) =>
  row && typeof row.explainability_evidence === "object"
    ? row.explainability_evidence
    : null;

export const constructLabel = (item) => {
  if (!item || typeof item !== "object") return "";
  if (item.label) return String(item.label);
  return CONSTRUCT_LABELS[item.construct] || "";
};

export const directionWord = (item) => {
  const construct = item?.construct;
  const direction = item?.direction === "lower" ? "lower" : "higher";
  return (DIRECTION_WORD[construct] || DIRECTION_WORD.size)[direction];
};

export const formatMetric = (value) => {
  if (value == null || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return String(value);
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
};

export const comparisonText = (item) => {
  if (item?.audience_value == null || item?.other_value == null) return "";
  return `${formatMetric(item.audience_value)} vs ${formatMetric(item.other_value)}`;
};

export const thresholdText = (item) => {
  const label = constructLabel(item);
  const op = item?.op;
  const threshold = item?.threshold;
  if (!label || !op || threshold == null || threshold === "") return "";
  return `${label} ${op} ${formatMetric(threshold)}`;
};

export const definingBehaviors = (evidence) =>
  Array.isArray(evidence?.defining_behavior) ? evidence.defining_behavior.filter(Boolean) : [];

export const supportingEvidence = (evidence) =>
  (Array.isArray(evidence?.supporting_evidence) ? evidence.supporting_evidence : []).filter(
    (item) => item && (constructLabel(item) || comparisonText(item))
  );

export const discoveryWhy = (evidence) => {
  const defining = definingBehaviors(evidence);
  if (!defining.length) return "";
  const facts = defining
    .map((item) => {
      const fact = (DIRECTION_FACT[item.construct] || {})[item.direction === "lower" ? "lower" : "higher"];
      return fact || null;
    })
    .filter(Boolean);
  if (!facts.length) return "";
  if (facts.length === 1) {
    return `Customers in this audience typically have ${facts[0]} than the rest of the file.`;
  }
  return `Customers in this audience typically have ${facts.join(" and ")}. Both behaviors define this audience.`;
};

export const discoverySteps = (evidence) => {
  const defining = definingBehaviors(evidence);
  const labels = defining.map(constructLabel).filter(Boolean);
  const pattern =
    labels.length > 1 ? labels.join(" + ") : labels[0] || "Behavioral pattern";
  return ["Data", pattern, "Independent evidence", "Audience"];
};

export const memberWhyText = (reasons) => {
  const parts = (Array.isArray(reasons) ? reasons : [])
    .map((item) => thresholdText(item) || constructLabel(item))
    .filter(Boolean);
  return parts.join(" AND ");
};

export const memberColumns = (evidence) => {
  const seen = new Set();
  const columns = [];
  definingBehaviors(evidence).forEach((item, index) => {
    const label = constructLabel(item);
    if (!label || seen.has(item.construct || label)) return;
    seen.add(item.construct || label);
    columns.push({
      key: `def-${item.construct || index}`,
      label,
      construct: item.construct,
      role: "defining",
    });
  });
  return columns;
};

export const memberCellValue = (member, column) => {
  const reasons = Array.isArray(member?.why_included) ? member.why_included : [];
  const match = reasons.find((item) => item.construct === column.construct);
  if (match?.observed_value != null) return formatMetric(match.observed_value);
  return "";
};

export const decisionSummary = (row) => {
  const decision = row?.decision;
  if (!decision || typeof decision !== "object") return "";
  return String(decision.summary || "");
};

export const decisionStatus = (row) =>
  row?.decision && typeof row.decision === "object" ? String(row.decision.status || "") : "";

export const hasStructuredEvidence = (evidence) => definingBehaviors(evidence).length > 0;

export const publicLeakTokens = (value) => {
  const blob = JSON.stringify(value || {}).toLowerCase();
  return INTERNAL_LEAK.filter((token) => blob.includes(token));
};
